// TelcoTrack — map UI for territorial overlay catalog (toggles, WMS paint, identify).
// Layer ids = AUTHORITY_OVERLAY_LAYERS. WMS is visualization; click identify uses WFS/ArcGIS.

import {
  AUTHORITY_OVERLAY_LAYER_IDS,
  AUTHORITY_OVERLAY_LAYERS,
  overlayLayerDef,
  type AuthorityOverlayLayerId,
} from './authority-territorial.constants';
import { DRAWING_IDS } from './drawing.constants';

export const MAP_FILTER_UZEMI_PREFIX = 'uzemi:';

export const AUTHORITY_OVERLAY_MAP_GROUP_IDS = [
  'voda',
  'ochrana',
  'pamatky',
  'nerosty',
  'lesy',
  'doprava',
] as const;
export type AuthorityOverlayMapGroupId = (typeof AUTHORITY_OVERLAY_MAP_GROUP_IDS)[number];

export interface AuthorityOverlayMapGroupDef {
  id: AuthorityOverlayMapGroupId;
  labelCs: string;
}

/** Group headers in the map layer filter (same UX as „Sítě DMVS TI“). */
export const AUTHORITY_OVERLAY_MAP_GROUPS: readonly AuthorityOverlayMapGroupDef[] = [
  { id: 'voda', labelCs: 'Území — voda a záplavy' },
  { id: 'ochrana', labelCs: 'Území — ochrana přírody' },
  { id: 'pamatky', labelCs: 'Území — památky a archeologie' },
  { id: 'nerosty', labelCs: 'Území — ložiska / CHLÚ' },
  { id: 'lesy', labelCs: 'Území — lesy' },
  { id: 'doprava', labelCs: 'Území — doprava' },
] as const;

export interface AuthorityOverlayMapLayerDef {
  layerId: AuthorityOverlayLayerId;
  groupId: AuthorityOverlayMapGroupId;
  /** Public WMS GetMap. Empty = no raster (identify still works). */
  wmsUrl: string;
  wmsLayers: string;
  wmsVersion?: '1.1.1' | '1.3.0';
  attribution: string;
}

const DIBAVOD_WMS = 'https://ags.vuv.cz/arcgis/services/isvs_voda/isvs_voda/MapServer/WMSServer';
const ROAD_WMS = 'https://geoportal.cuzk.cz/geoserver/tn-ro/wms';
const RAIL_WMS = 'https://geoportal.cuzk.cz/geoserver/tn-ra/wms';

export const AUTHORITY_OVERLAY_MAP_LAYERS: readonly AuthorityOverlayMapLayerDef[] = [
  {
    layerId: 'dibavod-voda',
    groupId: 'voda',
    wmsUrl: DIBAVOD_WMS,
    wmsLayers: 'UtvaryPOV_Reky,OPVZ_VodniZdroje',
    attribution: '© VÚV TGM – DIBAVOD',
  },
  {
    layerId: 'dibavod-zaplavy',
    groupId: 'voda',
    wmsUrl: DIBAVOD_WMS,
    wmsLayers: 'ZaplavUzemi_Q100,ZaplavUzemi_AktivniZony',
    attribution: '© VÚV TGM – DIBAVOD',
  },
  {
    layerId: 'dibavod-chopav',
    groupId: 'voda',
    wmsUrl: DIBAVOD_WMS,
    wmsLayers: 'CHOPAV',
    attribution: '© VÚV TGM – DIBAVOD',
  },
  {
    layerId: 'aopk-chko',
    groupId: 'ochrana',
    wmsUrl: 'https://gis.nature.cz/arcgis/services/UzemniOchrana/ChranUzemi/MapServer/WMSServer',
    wmsLayers: 'Velkoplošné_zvláště_chráněné_území,Maloplošné_zvláště_chráněné_území__MZCHÚ_',
    attribution: '© AOPK ČR',
  },
  {
    layerId: 'aopk-natura',
    groupId: 'ochrana',
    wmsUrl: 'https://gis.nature.cz/arcgis/services/Aplikace/Opendata/MapServer/WMSServer',
    wmsLayers: 'Ptaci_oblasti,Evropsky_vyznamne_lokality',
    attribution: '© AOPK ČR',
  },
  {
    layerId: 'npu-pamatky',
    groupId: 'pamatky',
    wmsUrl: 'https://geoportal.npu.cz/arcgis/services/INSPIRE/ProtectedSites/MapServer/WMSServer',
    wmsLayers: '0',
    attribution: '© NPÚ',
  },
  {
    layerId: 'npu-uan',
    groupId: 'pamatky',
    wmsUrl: 'https://geoportal.npu.cz/arcgis/services/ISAD/Uzemi_archeologickeho_nalezu/MapServer/WMSServer',
    wmsLayers: '2,3',
    attribution: '© NPÚ',
  },
  {
    layerId: 'cgs-chlu',
    groupId: 'nerosty',
    wmsUrl: 'https://mapy.geology.cz/arcgis/services/Suroviny/chranena_loziskova_uzemi/MapServer/WMSServer',
    wmsLayers: '0',
    attribution: '© ČGS',
  },
  {
    layerId: 'les-pupfl',
    groupId: 'lesy',
    wmsUrl: '',
    wmsLayers: '',
    attribution: '© Lesy ČR',
  },
  {
    layerId: 'rsd-silnice',
    groupId: 'doprava',
    wmsUrl: ROAD_WMS,
    wmsLayers: 'tn-ro:RoadLink',
    attribution: '© ČÚZK – TN-RO',
  },
  {
    layerId: 'sus-silnice',
    groupId: 'doprava',
    wmsUrl: ROAD_WMS,
    wmsLayers: 'tn-ro:RoadLink',
    attribution: '© ČÚZK – TN-RO',
  },
  {
    layerId: 'szdc-zeleznice',
    groupId: 'doprava',
    wmsUrl: RAIL_WMS,
    wmsLayers: 'tn-ra:RailwayLink',
    attribution: '© ČÚZK – TN-RA',
  },
] as const;

export const AUTHORITY_OVERLAY_MAP_LAYER_IDS: readonly AuthorityOverlayLayerId[] = AUTHORITY_OVERLAY_LAYER_IDS;

export const AUTHORITY_OVERLAY_VISIBLE_ON_DRAWING_IDS = [
  DRAWING_IDS.GENERAL,
  DRAWING_IDS.STAVBA,
] as const;

export function isAuthorityOverlayVisibleOnDrawing(drawingId: string | null | undefined): boolean {
  const id = drawingId ?? '';
  return (AUTHORITY_OVERLAY_VISIBLE_ON_DRAWING_IDS as readonly string[]).includes(id);
}

export function authorityOverlayMapLayer(layerId: string): AuthorityOverlayMapLayerDef | undefined {
  return AUTHORITY_OVERLAY_MAP_LAYERS.find((row) => row.layerId === layerId);
}

export function authorityOverlayMapGroupLabel(groupId: string): string {
  return AUTHORITY_OVERLAY_MAP_GROUPS.find((row) => row.id === groupId)?.labelCs ?? groupId;
}

export function mapFilterUzemiId(layerId: string): string {
  return `${MAP_FILTER_UZEMI_PREFIX}${layerId}`;
}

/** Full territory fill — muted vs stored křížení GeoJSON. */
export const AUTHORITY_OVERLAY_TERRITORY_WMS_OPACITY = 0.45;

export const AUTHORITY_OVERLAY_TERRITORY_STYLE_BY_GROUP: Record<
  AuthorityOverlayMapGroupId,
  { color: string; fillColor: string; weight: number; fillOpacity: number; opacity: number }
> = {
  voda: { color: '#1d4ed8', fillColor: '#3b82f6', weight: 2, fillOpacity: 0.22, opacity: 0.85 },
  ochrana: { color: '#15803d', fillColor: '#22c55e', weight: 2, fillOpacity: 0.22, opacity: 0.85 },
  pamatky: { color: '#6d28d9', fillColor: '#8b5cf6', weight: 2, fillOpacity: 0.22, opacity: 0.85 },
  nerosty: { color: '#a16207', fillColor: '#ca8a04', weight: 2, fillOpacity: 0.2, opacity: 0.85 },
  lesy: { color: '#166534', fillColor: '#4ade80', weight: 2, fillOpacity: 0.2, opacity: 0.8 },
  doprava: { color: '#334155', fillColor: '#64748b', weight: 3, fillOpacity: 0.08, opacity: 0.9 },
};

export function authorityOverlayTerritoryStyle(layerId: string): (typeof AUTHORITY_OVERLAY_TERRITORY_STYLE_BY_GROUP)[AuthorityOverlayMapGroupId] {
  const groupId = authorityOverlayMapLayer(layerId)?.groupId ?? 'ochrana';
  return AUTHORITY_OVERLAY_TERRITORY_STYLE_BY_GROUP[groupId];
}

/** Product default: territory overlays OFF until the user (or Úřady „Zobrazit na mapě“) opts in. */
export function defaultEnabledAuthorityOverlayLayerIds(): readonly AuthorityOverlayLayerId[] {
  return [];
}

/** Stored ST_Intersection / DTM clip around VYKOP. */
export const AUTHORITY_OVERLAY_CROSSING_STYLE = {
  color: '#ea580c',
  weight: 5,
  opacity: 1,
  fillColor: '#ea580c',
  fillOpacity: 0.32,
} as const;

/** Focused `?overlayObligation=` crossing. */
export const AUTHORITY_OVERLAY_CROSSING_FOCUS_STYLE = {
  color: '#c026d3',
  weight: 7,
  opacity: 1,
  fillColor: '#c026d3',
  fillOpacity: 0.38,
} as const;

/** Matching territorial polygon after Úřady „Zobrazit na mapě“ (live WFS layer). */
export const AUTHORITY_OVERLAY_TERRITORY_FOCUS_STYLE = {
  color: '#c026d3',
  weight: 5,
  opacity: 1,
  fillColor: '#c026d3',
  fillOpacity: 0.35,
} as const;

/** Uložené průniky trasy VYKOP × území/síť (Úřady) — master toggle ve filtru Vrstvy. */
export const KRIZENI_TRAS_LAYER_ID = 'krizeni-tras' as const;
export const KRIZENI_TRAS_LAYER_LABEL = 'Křížení tras';
export const KRIZENI_TRAS_LAYER_GROUP = 'Úřady';
export const MAP_FILTER_KRIZENI_TRAS_ID = `overlay:${KRIZENI_TRAS_LAYER_ID}` as const;

export function authorityOverlayLayerLabel(layerId: string): string {
  return overlayLayerDef(layerId)?.labelCs ?? layerId;
}

/** Catalog completeness: every overlay layer has a map-display row. */
export function authorityOverlayMapCatalogComplete(): boolean {
  const ids = new Set(AUTHORITY_OVERLAY_MAP_LAYERS.map((row) => row.layerId));
  return AUTHORITY_OVERLAY_LAYERS.every((row) => ids.has(row.layerId));
}
